// OIDC Authorization Code + PKCE login against Mayar auth-v2 for www.
//
// www shares Mayar's database, so — exactly like www-wallet — it stores ONE
// token: the Mayar token pair ("<access>,<refresh>") returned by POST /token,
// written into the SAME `token` cookie www already consumes (comma-joined
// access,refresh). No JIT provisioning, no second internal token.
//
// This module is modeled on www-wallet/src/utils/auth.js. It is purely additive:
// the existing "Signin With Google" producer flow (AuthSignIn/signin.jsx) is left
// untouched — this is the net-new CONSUMER of the auth-v2 warm /authorize path.
//
// Flow:
//   beginLogin({ silent }) builds the /authorize URL with a freshly generated
//                          PKCE challenge + CSRF state (stashed in sessionStorage).
//                          Interactive: top-level redirect to /authorize. Silent
//                          (`prompt=none`): returns the URL without navigating so
//                          the caller can probe via hidden iframe / bounce-back;
//                          on a warm Mayar IdP session auth-v2 returns to
//                          /oidc/callback with a code and no prompt, otherwise
//                          `login_required`.
//   completeLogin({code,state}) validates state, exchanges the code (+ PKCE
//                          verifier) at /token, and stores the Mayar token pair
//                          into the existing `token` cookie.

import Cookies from 'js-cookie';
import { setAuthTokenCookie } from './auth';

const fetch = window.fetch.bind(window);

const getAuthUrl = () => {
  const envUrl = process.env.REACT_APP_MAYAR_AUTH_URL;
  if (envUrl && !envUrl.includes('mayar.club') && !envUrl.includes('mayar.id')) {
    // eslint-disable-next-line no-console
    console.warn('[oidcAuth] getAuthUrl from envUrl=%s', envUrl);
    return envUrl;
  }
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  let url = null;
  if (host.includes('mayar.io')) url = 'https://auth.mayar.io';
  else if (host.includes('mayar.so')) url = 'https://auth.mayar.so';
  else if (host.includes('mayar.club')) url = 'https://auth.mayar.club';
  else if (host.includes('mayar.id')) url = 'https://auth.mayar.id';
  else url = envUrl || null;
  // eslint-disable-next-line no-console
  console.warn('[oidcAuth] getAuthUrl host=%s => %s', host, url);
  return url;
};

const AUTH_URL = getAuthUrl();
const CLIENT_ID = process.env.REACT_APP_OIDC_CLIENT_ID;

const VERIFIER_KEY = 'oidc_code_verifier';
const STATE_KEY = 'oidc_state';
const ATTEMPTS_KEY = 'oidc_attempts';
const MAX_SILENT_ATTEMPTS = 2; // break a warm-path loop if a minted token keeps failing

// mayar-www is registered against `${origin}/oidc/callback` for every env (see
// auth-v2 oidc_clients.json). It must NOT be `/auth`: on the deployed hosts
// traefik routes `Host:web.mayar.*;PathPrefixStrip:/auth` to the auth-gateway, so
// anything under `/auth` (including the OIDC callback) never reaches this SPA and
// 404s at the gateway. `/oidc/callback` avoids that prefix. redirect_uri must
// match exactly on both /authorize and /token, so both legs derive it from here.
const redirectUri = () => `${window.location.origin}/oidc/callback`;

export const getToken = () => Cookies.get('token') || null;

// Reset the silent-retry counter. Called (a) once a session has actually been
// accepted by the API (/me OK in App bootstrap) and (b) on a cold-miss landing
// (/sign-in?sso=none) — neither is a failing token, so the warm path may retry
// freely. The counter is therefore left UNTOUCHED only across warm-but-rejected
// cycles, which is exactly the loop MAX_SILENT_ATTEMPTS must cap.
export const markAuthenticated = () => window.sessionStorage.removeItem(ATTEMPTS_KEY);

const base64Url = bytes => {
  let str = '';
  for (const byte of bytes) str += String.fromCharCode(byte);
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomBase64Url = (size = 32) => base64Url(window.crypto.getRandomValues(new Uint8Array(size)));

const sha256Base64Url = async text => {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return base64Url(new Uint8Array(digest));
};

// Generate a fresh PKCE verifier + CSRF state, persist them for the /token leg,
// and build the /authorize URL. `silent` adds `prompt=none` so auth-v2 returns
// `login_required` on a cold session instead of bouncing to Google.
const buildAuthorizeUrl = async ({ silent = false } = {}) => {
  const codeVerifier = randomBase64Url(32);
  const state = randomBase64Url(16);
  window.sessionStorage.setItem(VERIFIER_KEY, codeVerifier);
  window.sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid email',
    state,
    code_challenge: await sha256Base64Url(codeVerifier),
    code_challenge_method: 'S256'
  });
  if (silent) params.set('prompt', 'none');

  return `${AUTH_URL}/authorize?${params}`;
};

// Start the OIDC Authorization Code + PKCE flow.
//   interactive: top-level redirect to /authorize (returns the URL too).
//   silent (`prompt=none`): returns the URL WITHOUT navigating, so the caller
//     drives the hidden-iframe / bounce-back probe. Bounded by MAX_SILENT_ATTEMPTS
//     so a token that keeps being rejected can't spin an invisible redirect loop.
// Returns null when auth-v2 is not configured (missing env) — the caller falls
// back to the normal /sign-in page (the Google producer flow stays available).
export const beginLogin = async ({ silent = false } = {}) => {
  if (!AUTH_URL || !CLIENT_ID) return null;

  if (silent) {
    const attempts = Number(window.sessionStorage.getItem(ATTEMPTS_KEY) || '0');
    if (attempts >= MAX_SILENT_ATTEMPTS) {
      window.sessionStorage.removeItem(ATTEMPTS_KEY);
      return null;
    }
    window.sessionStorage.setItem(ATTEMPTS_KEY, String(attempts + 1));
  }

  const url = await buildAuthorizeUrl({ silent });

  if (!silent) window.location.assign(url);
  return url;
};

// Exchange the authorization code (+ PKCE verifier) for the Mayar token pair and
// store it. Returns the token pair on success; throws on state/PKCE/exchange
// failure.
//
// De-duped at module scope: an auth code is single-use (the server deletes it on
// exchange) and React StrictMode mounts the callback effect twice in dev. Without
// this guard the second call would find the verifier already consumed, throw, and
// its caller would navigate away mid-flight — aborting the first exchange before
// the token cookie is set, spinning an /authorize -> /token redirect loop.
let pendingExchange = null;

const exchangeCodeForToken = async ({ code, state }) => {
  const expectedState = window.sessionStorage.getItem(STATE_KEY);
  const codeVerifier = window.sessionStorage.getItem(VERIFIER_KEY);
  window.sessionStorage.removeItem(STATE_KEY);
  window.sessionStorage.removeItem(VERIFIER_KEY);

  if (!code || !codeVerifier || !state || state !== expectedState) {
    throw new Error('invalid_state'); // PKCE/state desync — terminal
  }

  const res = await fetch(`${AUTH_URL}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      code_verifier: codeVerifier,
      client_id: CLIENT_ID,
      redirect_uri: redirectUri()
    })
  });
  if (!res.ok) throw new Error('token_exchange_failed');

  const { access_token, refresh_token } = await res.json();
  if (!access_token || !refresh_token) throw new Error('token_exchange_failed');

  // www shares Mayar's DB: store the Mayar token pair directly (no JIT), reusing
  // www's existing cookie writer so the shape/domain match what www already reads.
  const tokenPair = `${access_token},${refresh_token}`;
  setAuthTokenCookie(tokenPair);
  // NB: do NOT reset the silent-retry counter here. The token is set but not yet
  // proven acceptable — /me could still 401 (disabled user, etc.), which reloads
  // back through the warm probe. Resetting now would defeat MAX_SILENT_ATTEMPTS
  // and spin an infinite warm -> /me 401 -> reload loop. The counter is reset on
  // genuine /me success (App bootstrap) or a cold miss (/sign-in?sso=none).
  return tokenPair;
};

export const completeLogin = ({ code, state }) => {
  if (pendingExchange) return pendingExchange;
  pendingExchange = exchangeCodeForToken({ code, state }).finally(() => {
    pendingExchange = null;
  });
  return pendingExchange;
};
